import { FeatureFlagsService } from './FeatureFlagsService';

interface RouteConfig {
  flagKey: string;
  transformResponse?: (data: any) => any;
  overrideUrl?: (originalUrl: string) => string;
  overrideMethod?: string;
}

export class ApiMigrationService {
  // 1. EXACT MATCH REGISTRY (Fast lookup for static paths)
  // eslint-disable-next-line
  static registry: Record<string, (params: URLSearchParams) => RouteConfig | null> = {
    '/api/v1/staticdata': () => {
      return {
        flagKey: 'migrate-api_v3-staticdata',
        transformResponse: (v3Data) => ({
          data: v3Data,
          success: true
        })
      };
    },
    '/api/v1/users/find-all': () => {
      return {
        flagKey: 'migrate-api_v3-users',
        transformResponse: (v3Data) => ({
          data: v3Data,
          success: true
        })
      };
    },
    '/api/v1/users/roles': () => {
      return {
        flagKey: 'migrate-api_v3-users'
      };
    },
    '/api/v1/users/search-emails': () => {
      return {
        flagKey: 'migrate-api_v3-users'
      };
    },
    '/api/v1/users/initializeRole': () => {
      return {
        flagKey: 'migrate-api_v3-users'
      }
    },
    '/api/v1/operator/register': () => {
      return {
        flagKey: 'migrate-api_v3-operator'
      }
    },
    '/api/v1/machines/get': () => {
      return {
        flagKey: 'migrate-api_v3-machine',
        overrideUrl: (originalUrl) => {
          const correctedPath = originalUrl.replace('/api/v1/machines/get', '/api/v1/machine/s/get');
          return `/api/v3${correctedPath}`;
        },
        transformResponse: (v3Data) => ({
          data: v3Data,
          success: true
        })
      }
    },
    '/api/v1/machine/save': () => {
      return {
        flagKey: 'migrate-api_v3-machine'
      }
    },
    '/api/v1/notifications': () => {
      return {
        flagKey: 'migrate-api_v3-notifications'
      }
    },
    '/api/v1/notifications/markAllRead': () => {
      return {
        flagKey: 'migrate-api_v3-notifications'
      }
    },
    '/api/v1/printer/generateLabel': () => {
      return {
        flagKey: 'migrate-api_v3-printer'
      }
    },
    '/api/v1/printer/generateNewLabel': () => {
      return {
        flagKey: 'migrate-api_v3-printer'
      }
    },
    '/api/v1/businesspartnergroups': () => {
      return {
        flagKey: 'migrate-api_v3-customergroup',
        transformResponse: (v3Data) => ({
          data: v3Data,
          success: true
        })
      }
    },
    '/api/v1/businesspartners/assignableUsers': () => {
      return {
        flagKey: 'migrate-api_v3-customer',
        transformResponse: (v3Data) => ({
          data: v3Data,
          success: true
        })
      }
    },
    '/api/v2/customers': () => {
      return {
        flagKey: 'migrate-api_v3-customer',
        transformResponse: (v3Data) => ({
          data: v3Data,
          success: true
        })
      }
    },
    '/api/v2/my-customers': () => {
      return {
        flagKey: 'migrate-api_v3-customer',
        transformResponse: (v3Data) => ({
          data: v3Data,
          success: true
        })
      }
    },
    '/api/v1/businesspartners/addCustomer': () => {
      return {
        flagKey: 'migrate-api_v3-customer'
      }
    }
  };

  // 2. DYNAMIC REGISTRY (Regex lookup for path variables)
  static dynamicRegistry: Array<{
    regex: RegExp;
    handler: (match: RegExpMatchArray, params: URLSearchParams) => RouteConfig | null;
  }> = [
    {
      regex: /^\/api\/v2\/users\/([^/]+)\/force-logout$/,
      // eslint-disable-next-line
      handler: (match, params) => {
        // match[1] contains the actual user ID (e.g., '12345') if you ever need it for complex logic
        return {
          flagKey: 'migrate-api_v3-users'
        };
      }
    },
    {
      regex: /^\/api\/v1\/users\/([^/]+)\/delete$/,
      handler: () => {
        return {
          flagKey: 'migrate-api_v3-users'
        };
      }
    },
    {
      regex: /^\/api\/v1\/machine\/([^/]+)\/remove$/,
      handler: () => {
        return {
          flagKey: 'migrate-api_v3-machine'
        };
      }
    },
    {
      regex: /^\/api\/v1\/machine\/([^/]+)$/,
      handler: () => {
        return {
          flagKey: 'migrate-api_v3-machine',
          transformResponse: (v3Data) => ({
            data: v3Data,
            success: true
          })
        };
      }
    },
    {
      regex: /^\/api\/v1\/notifications\/([^/]+)\/mark(Read|Unread)$/,
      handler: () => {
        return {
          flagKey: 'migrate-api_v3-notifications'
        };
      }
    },
    {
      regex: /^\/api\/v1\/businesspartnergroups\/([^/]+)$/,
      handler: () => {
        return {
          flagKey: 'migrate-api_v3-customergroup'
        };
      }
    },
    {
      regex: /^\/api\/v2\/customers\/([^/]+)$/,
      handler: () => {
        return {
          flagKey: 'migrate-api_v3-customer',
          transformResponse: (v3Data) => ({
            data: v3Data,
            success: true
          })
        };
      }
    },
    {
      regex: /^\/api\/v1\/customer\/([^/]+)\/approve-(prospect|customer)$/,
      handler: () => {
        return {
          flagKey: 'migrate-api_v3-customer',
          overrideMethod: 'PATCH'
        };
      }
    },
    {
      regex: /^\/api\/v1\/businesspartners\/([^/]+)$/,
      handler: () => {
        return {
          flagKey: 'migrate-api_v3-customer',
        };
      }
    },
    {
      regex: /^\/api\/v1\/customer\/([^/]+)\/(disable|enable)-customer$/,
      handler: () => {
        return {
          flagKey: 'migrate-api_v3-customer',
          overrideMethod: 'PATCH'
        };
      }
    },
    {
      regex: /^\/api\/v1\/customer\/([^/]+)\/delete$/,
      handler: () => {
        return {
          flagKey: 'migrate-api_v3-customer',
          overrideMethod: 'PATCH'
        };
      }
    },
    {
      regex: /^\/api\/v1\/customer\/([^/]+)\/national-address$/,
      handler: () => {
        return {
          flagKey: 'migrate-api_v3-customer',
          overrideMethod: 'GET'
        };
      }
    },
    {
      regex: /^\/api\/v2\/customers\/([^/]+)\/discount$/,
      handler: () => {
        return {
          flagKey: 'migrate-api_v3-customer'
        };
      }
    }
  ];

  // 3. THE CORE ROUTER
  static getRoutedApi(originalUrl: string, requestData: any) {
    try {
      const [basePath, queryString] = originalUrl.split('?');
      const params = new URLSearchParams(queryString || '');
      let config: RouteConfig | null = null;
      // STEP A: Try to find an exact match first (Fastest)
      const staticHandler = ApiMigrationService.registry[basePath];
      if (staticHandler) {
        config = staticHandler(params);
      } 
      // STEP B: If no exact match, check dynamic regex patterns
      else {
        const dynamicRoute = ApiMigrationService.dynamicRegistry.find(route => route.regex.test(basePath));
        if (dynamicRoute) {
          const match = basePath.match(dynamicRoute.regex);
          if (match) {
            config = dynamicRoute.handler(match, params);
          }
        }
      }
      // STEP C: Evaluate the feature flag if a config was found
      if (config && FeatureFlagsService.checkStatus(config.flagKey)) {
        const finalUrl = config.overrideUrl ? config.overrideUrl(originalUrl) : `/api/v3${originalUrl}`;
        return {
          resolvedUrl: finalUrl, 
          requestData, 
          isMigrated: true,
          transformResponse: config.transformResponse,
          overrideMethod: config.overrideMethod
        };
      }
    } catch (error) {
      console.error(`Migration Router Error for ${originalUrl}:`, error);
    }

    // Fallback
    return { resolvedUrl: originalUrl, requestData, isMigrated: false, transformResponse: undefined };
  }
}